import React, { useCallback, forwardRef } from 'react';
import './ActionLink.less';
import classNames from 'classnames';
import { useTranslatedPhrase } from 'stores/TranslationStore';

export interface IActionLinkProps {
  label: string;
  value: any;
  onClick?: (value: any) => void;
  valueSet?: string[];
  className?: string;
  linkClassName?: string;
  onMouseOver?: (label: string) => void;
  children?: React.ReactNode;
  isActive?: boolean;
  disabled?: boolean;
  afterLabel?: React.ReactNode;
  /** Content, such as '...' to go after a translatable string, etc. will be rendered inside the label HTML element. */
  suffix?: React.ReactNode;
  beforeLabel?: React.ReactNode;
  translateLabel?: boolean;
  /**
   * Primary actions, for example, are emphasized.
   */
  emphasized?: boolean;
  readonly?: boolean;
}

const getActionLinkClassName = (className?: string, isActive?: boolean, emphasized?: boolean, readonly?: boolean) =>
  classNames('dpc-action-link', className, {
    'dpc-action-link--active': isActive,
    'dpc-action-link--emphasized': emphasized,
    'dpc-action-link--readonly': readonly,
  });

/**
 * Action link component, for action call-outs. Functionally a button (no href) that looks like a link.
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export const ActionLink = forwardRef(
  (
    {
      afterLabel,
      beforeLabel,
      // there was no discernable reason in the code for this, but orignal 'callback' stopped making it here.
      // so renamed to onClick. perhaps 'callback' is internally used / reserved.
      onClick: callback,
      className,
      linkClassName,
      children,
      disabled,
      emphasized,
      readonly,
      isActive,
      onMouseOver,
      label: defaultLabel,
      suffix,
      translateLabel,
      value,
    }: IActionLinkProps,
    ref: React.Ref<HTMLElement>
  ) => {
    const label = useTranslatedPhrase(defaultLabel, !!translateLabel);
    const onClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        callback && callback(value);
      },
      [callback, value]
    );

    const mouseOver = useCallback(() => {
      onMouseOver && onMouseOver(label);
    }, [label, onMouseOver]);

    return (
      // TODO should be li, but making action links callout a ul is another chore, since callout body is always div
      <div
        onMouseOver={mouseOver}
        className={getActionLinkClassName(className, isActive, emphasized, readonly)}
        ref={ref as any} // any because TS is too strict about HTML elements for this scenario
      >
        {/* make "children" replace what's here for full flexibility */
        children ? (
          children
        ) : (
          <>
            {beforeLabel}
            <button
              className={classNames('dpc-action-link__text', linkClassName)}
              title={label}
              disabled={disabled}
              onClick={onClick}
            >
              {/* string literal here so it's evaluated as one value for things like automated tests: */}
              {`${label}${suffix || ''}`}
            </button>
            {afterLabel}
          </>
        )}
      </div>
    );
  }
);
